import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { notify } from 'components';

// =============================
// TICKETS
// =============================

export const loadTickets = createAsyncThunk(
  'support/loadTickets',
  async ({ page = 1, searchValue = '', page_size = 20, filters = {} }, { rejectWithValue }) => {
    try {
      let url = `support/tickets/?page=${page}&page_size=${page_size}`;
      if (searchValue) url += `&search=${searchValue}`;
      if (filters.status) url += `&status=${filters.status}`;
      if (filters.priority) url += `&priority=${filters.priority}`;
      if (filters.category) url += `&category=${filters.category}`;
      if (filters.project) url += `&project=${filters.project}`;
      if (filters.assigned_to_me) url += `&assigned_to_me=true`;

      const response = await axios.get(url);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

export const loadTicketById = createAsyncThunk(
  'support/loadTicketById',
  async (ticketId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`support/tickets/${ticketId}/`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

export const assignTicket = createAsyncThunk(
  'support/assignTicket',
  async ({ ticketId, employee_id, assigned_team }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`support/tickets/${ticketId}/assign/`, {
        employee_id,
        assigned_team
      });
      notify('success', 'Ticket assigned successfully');
      return response.data;
    } catch (err) {
      notify('error', 'Failed to assign ticket');
      return rejectWithValue(err.response?.data);
    }
  }
);

export const changeTicketStatus = createAsyncThunk(
  'support/changeTicketStatus',
  async ({ ticketId, status_id, sub_status_id }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`support/tickets/${ticketId}/change_status/`, {
        status_id,
        sub_status_id
      });
      notify('success', 'Status updated successfully');
      return response.data;
    } catch (err) {
      notify('error', 'Failed to update status');
      return rejectWithValue(err.response?.data);
    }
  }
);

export const addTicketComment = createAsyncThunk(
  'support/addTicketComment',
  async ({ ticketId, comment_type, message, is_internal = false, attachment_ids = [] }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`support/tickets/${ticketId}/add_comment/`, {
        ticket: ticketId,
        comment_type,
        message,
        source: 'WEB',
        is_internal,
        attachments: attachment_ids
      });
      notify('success', 'Comment added successfully');
      return response.data;
    } catch (err) {
      notify('error', 'Failed to add comment');
      return rejectWithValue(err.response?.data);
    }
  }
);

export const convertToTasks = createAsyncThunk(
  'support/convertToTasks',
  async ({ ticketId, tasks, assigneeUserIds }, { rejectWithValue }) => {
    try {
      // Each task may carry its own assignee_user_ids (user.User ids); the
      // top-level list is the fallback for tasks that do not.
      const payload = { tasks };
      if (assigneeUserIds && assigneeUserIds.length) {
        payload.assignee_user_ids = assigneeUserIds;
      }
      const response = await axios.post(`support/tickets/${ticketId}/convert_to_tasks/`, payload);
      notify('success', `Created ${tasks.length} task(s) from ticket`);
      return response.data;
    } catch (err) {
      const detail = err.response?.data?.error;
      notify('error', detail ? `Failed to convert to tasks: ${detail}` : 'Failed to convert to tasks');
      return rejectWithValue(err.response?.data);
    }
  }
);

export const updateTicket = createAsyncThunk(
  'support/updateTicket',
  async ({ ticketId, ticketData }, { rejectWithValue }) => {
    try {
      const response = await axios.patch(`support/tickets/${ticketId}/`, ticketData);
      notify('success', 'Ticket updated successfully');
      return response.data;
    } catch (err) {
      notify('error', 'Failed to update ticket');
      return rejectWithValue(err.response?.data);
    }
  }
);

export const deleteTicket = createAsyncThunk(
  'support/deleteTicket',
  async (ticketId, { rejectWithValue }) => {
    try {
      await axios.delete(`support/tickets/${ticketId}/`);
      notify('success', 'Ticket deleted successfully');
      return ticketId;
    } catch (err) {
      notify('error', 'Failed to delete ticket');
      return rejectWithValue(err.response?.data);
    }
  }
);

export const loadTicketStatistics = createAsyncThunk(
  'support/loadTicketStatistics',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('support/tickets/statistics/');
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

// =============================
// ACCOUNT APPROVALS
// =============================

export const loadAccountApprovals = createAsyncThunk(
  'support/loadAccountApprovals',
  async ({ page = 1, searchValue = '', status = 'pending', page_size = 20 }, { rejectWithValue }) => {
    try {
      let url = `support/account-approvals/?page=${page}&page_size=${page_size}&status=${status}`;
      if (searchValue) url += `&search=${searchValue}`;

      const response = await axios.get(url);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

export const approveAccount = createAsyncThunk(
  'support/approveAccount',
  async ({ accountId, frontend_url }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`support/account-approvals/${accountId}/approve/`, {
        frontend_url: frontend_url || 'http://localhost:3001'
      });
      notify('success', 'Account approved successfully');
      return response.data;
    } catch (err) {
      notify('error', 'Failed to approve account');
      return rejectWithValue(err.response?.data);
    }
  }
);

export const rejectAccount = createAsyncThunk(
  'support/rejectAccount',
  async ({ accountId, reason }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`support/account-approvals/${accountId}/reject/`, {
        reason
      });
      notify('success', 'Account rejected');
      return response.data;
    } catch (err) {
      notify('error', 'Failed to reject account');
      return rejectWithValue(err.response?.data);
    }
  }
);

export const loadApprovalStatistics = createAsyncThunk(
  'support/loadApprovalStatistics',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('support/account-approvals/statistics/');
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

// =============================
// CONFIGURATION
// =============================

export const loadTicketStatuses = createAsyncThunk(
  'support/loadTicketStatuses',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('support/ticket-statuses/');
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

export const loadTicketCategories = createAsyncThunk(
  'support/loadTicketCategories',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('support/ticket-categories/');
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

export const loadSLAPolicies = createAsyncThunk(
  'support/loadSLAPolicies',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('support/sla-policies/');
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

// CRUD Operations for Ticket Statuses
export const createTicketStatus = createAsyncThunk(
  'support/createTicketStatus',
  async (statusData, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.post('support/ticket-statuses/', statusData);
      notify('success', 'Status created successfully');
      dispatch(loadTicketStatuses());
      return response.data;
    } catch (err) {
      notify('error', 'Failed to create status');
      return rejectWithValue(err.response?.data);
    }
  }
);

export const updateTicketStatus = createAsyncThunk(
  'support/updateTicketStatus',
  async ({ id, ...statusData }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.patch(`support/ticket-statuses/${id}/`, statusData);
      notify('success', 'Status updated successfully');
      dispatch(loadTicketStatuses());
      return response.data;
    } catch (err) {
      notify('error', 'Failed to update status');
      return rejectWithValue(err.response?.data);
    }
  }
);

export const deleteTicketStatus = createAsyncThunk(
  'support/deleteTicketStatus',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      await axios.delete(`support/ticket-statuses/${id}/`);
      notify('success', 'Status deleted successfully');
      dispatch(loadTicketStatuses());
      return id;
    } catch (err) {
      notify('error', 'Failed to delete status');
      return rejectWithValue(err.response?.data);
    }
  }
);

// CRUD Operations for Ticket Categories
export const createTicketCategory = createAsyncThunk(
  'support/createTicketCategory',
  async (categoryData, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.post('support/ticket-categories/', categoryData);
      notify('success', 'Category created successfully');
      dispatch(loadTicketCategories());
      return response.data;
    } catch (err) {
      notify('error', 'Failed to create category');
      return rejectWithValue(err.response?.data);
    }
  }
);

export const updateTicketCategory = createAsyncThunk(
  'support/updateTicketCategory',
  async ({ id, ...categoryData }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.patch(`support/ticket-categories/${id}/`, categoryData);
      notify('success', 'Category updated successfully');
      dispatch(loadTicketCategories());
      return response.data;
    } catch (err) {
      notify('error', 'Failed to update category');
      return rejectWithValue(err.response?.data);
    }
  }
);

export const deleteTicketCategory = createAsyncThunk(
  'support/deleteTicketCategory',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      await axios.delete(`support/ticket-categories/${id}/`);
      notify('success', 'Category deleted successfully');
      dispatch(loadTicketCategories());
      return id;
    } catch (err) {
      notify('error', 'Failed to delete category');
      return rejectWithValue(err.response?.data);
    }
  }
);

// CRUD Operations for SLA Policies
export const createSLAPolicy = createAsyncThunk(
  'support/createSLAPolicy',
  async (policyData, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.post('support/sla-policies/', policyData);
      notify('success', 'SLA Policy created successfully');
      dispatch(loadSLAPolicies());
      return response.data;
    } catch (err) {
      notify('error', 'Failed to create SLA policy');
      return rejectWithValue(err.response?.data);
    }
  }
);

export const updateSLAPolicy = createAsyncThunk(
  'support/updateSLAPolicy',
  async ({ id, ...policyData }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.patch(`support/sla-policies/${id}/`, policyData);
      notify('success', 'SLA Policy updated successfully');
      dispatch(loadSLAPolicies());
      return response.data;
    } catch (err) {
      notify('error', 'Failed to update SLA policy');
      return rejectWithValue(err.response?.data);
    }
  }
);

export const deleteSLAPolicy = createAsyncThunk(
  'support/deleteSLAPolicy',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      await axios.delete(`support/sla-policies/${id}/`);
      notify('success', 'SLA Policy deleted successfully');
      dispatch(loadSLAPolicies());
      return id;
    } catch (err) {
      notify('error', 'Failed to delete SLA policy');
      return rejectWithValue(err.response?.data);
    }
  }
);

// =============================
// KNOWLEDGE BASE
// =============================

// The list's current search and page size, so reloading after a create/edit/
// delete keeps them instead of snapping back to 20 rows.
let lastKBQuery = {};

export const loadKBArticles = createAsyncThunk(
  'support/loadKBArticles',
  async ({ page = 1, searchValue = '', page_size = 20 }, { rejectWithValue }) => {
    lastKBQuery = { searchValue, page_size };
    try {
      let url = `support/kb-articles/?page=${page}&page_size=${page_size}`;
      if (searchValue) url += `&search=${searchValue}`;

      const response = await axios.get(url);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

export const createKBArticle = createAsyncThunk(
  'support/createKBArticle',
  async (articleData, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.post('support/kb-articles/', articleData);
      notify('success', 'Article created successfully');
      dispatch(loadKBArticles({ ...lastKBQuery, page: 1 }));
      return response.data;
    } catch (err) {
      notify('error', 'Failed to create article');
      return rejectWithValue(err.response?.data);
    }
  }
);

export const updateKBArticle = createAsyncThunk(
  'support/updateKBArticle',
  async ({ id, ...articleData }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.patch(`support/kb-articles/${id}/`, articleData);
      notify('success', 'Article updated successfully');
      dispatch(loadKBArticles({ ...lastKBQuery, page: 1 }));
      return response.data;
    } catch (err) {
      notify('error', 'Failed to update article');
      return rejectWithValue(err.response?.data);
    }
  }
);

export const deleteKBArticle = createAsyncThunk(
  'support/deleteKBArticle',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      await axios.delete(`support/kb-articles/${id}/`);
      notify('success', 'Article deleted successfully');
      dispatch(loadKBArticles({ ...lastKBQuery, page: 1 }));
      return id;
    } catch (err) {
      notify('error', 'Failed to delete article');
      return rejectWithValue(err.response?.data);
    }
  }
);

// =============================
// TICKET TEMPLATES
// =============================

export const loadTicketTemplates = createAsyncThunk(
  'support/loadTicketTemplates',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('support/ticket-templates/');
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

export const createTicketTemplate = createAsyncThunk(
  'support/createTicketTemplate',
  async (templateData, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.post('support/ticket-templates/', templateData);
      notify('success', 'Template created successfully');
      dispatch(loadTicketTemplates());
      return response.data;
    } catch (err) {
      notify('error', 'Failed to create template');
      return rejectWithValue(err.response?.data);
    }
  }
);

export const updateTicketTemplate = createAsyncThunk(
  'support/updateTicketTemplate',
  async ({ id, ...templateData }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.patch(`support/ticket-templates/${id}/`, templateData);
      notify('success', 'Template updated successfully');
      dispatch(loadTicketTemplates());
      return response.data;
    } catch (err) {
      notify('error', 'Failed to update template');
      return rejectWithValue(err.response?.data);
    }
  }
);

export const deleteTicketTemplate = createAsyncThunk(
  'support/deleteTicketTemplate',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      await axios.delete(`support/ticket-templates/${id}/`);
      notify('success', 'Template deleted successfully');
      dispatch(loadTicketTemplates());
      return id;
    } catch (err) {
      notify('error', 'Failed to delete template');
      return rejectWithValue(err.response?.data);
    }
  }
);

const supportSlice = createSlice({
  name: 'support',
  initialState: {
    // Tickets
    tickets: [],
    selectedTicket: null,
    ticketsLoading: false,
    ticketsError: null,
    ticketsPagination: {
      currentPage: 1,
      totalPages: 1,
      totalCount: 0
    },
    ticketStatistics: null,

    // Account Approvals
    approvals: [],
    approvalsLoading: false,
    approvalsError: null,
    approvalsPagination: {
      currentPage: 1,
      totalPages: 1,
      totalCount: 0
    },
    approvalStatistics: null,

    // Configuration
    ticketStatuses: [],
    ticketCategories: [],
    slaPolicies: [],
    configLoading: false,

    // Knowledge Base
    kbArticles: [],
    kbLoading: false,
    kbPagination: {
      currentPage: 1,
      totalPages: 1,
      totalCount: 0
    },

    // Ticket Templates
    ticketTemplates: [],
    templatesLoading: false,
  },
  reducers: {
    clearSelectedTicket: (state) => {
      state.selectedTicket = null;
    },
  },
  extraReducers: (builder) => {
    // Load Tickets
    builder.addCase(loadTickets.pending, (state) => {
      state.ticketsLoading = true;
      state.ticketsError = null;
    });
    builder.addCase(loadTickets.fulfilled, (state, action) => {
      state.ticketsLoading = false;
      state.tickets = action.payload.results || [];
      state.ticketsPagination = {
        currentPage: action.payload.page || 1,
        totalPages: Math.ceil((action.payload.count || 0) / 20),
        totalCount: action.payload.count || 0
      };
    });
    builder.addCase(loadTickets.rejected, (state, action) => {
      state.ticketsLoading = false;
      state.ticketsError = action.payload;
    });

    // Load Ticket By ID
    builder.addCase(loadTicketById.pending, (state) => {
      state.ticketsLoading = true;
    });
    builder.addCase(loadTicketById.fulfilled, (state, action) => {
      state.ticketsLoading = false;
      state.selectedTicket = action.payload;
    });
    builder.addCase(loadTicketById.rejected, (state, action) => {
      state.ticketsLoading = false;
      state.ticketsError = action.payload;
    });

    // Ticket Statistics
    builder.addCase(loadTicketStatistics.fulfilled, (state, action) => {
      state.ticketStatistics = action.payload;
    });

    // Account Approvals
    builder.addCase(loadAccountApprovals.pending, (state) => {
      state.approvalsLoading = true;
      state.approvalsError = null;
    });
    builder.addCase(loadAccountApprovals.fulfilled, (state, action) => {
      state.approvalsLoading = false;
      state.approvals = action.payload.results || action.payload || [];
      if (action.payload.count) {
        state.approvalsPagination = {
          currentPage: action.payload.page || 1,
          totalPages: Math.ceil((action.payload.count || 0) / 20),
          totalCount: action.payload.count || 0
        };
      }
    });
    builder.addCase(loadAccountApprovals.rejected, (state, action) => {
      state.approvalsLoading = false;
      state.approvalsError = action.payload;
    });

    // Approval Statistics
    builder.addCase(loadApprovalStatistics.fulfilled, (state, action) => {
      state.approvalStatistics = action.payload;
    });

    // Ticket Statuses
    builder.addCase(loadTicketStatuses.pending, (state) => {
      state.configLoading = true;
    });
    builder.addCase(loadTicketStatuses.fulfilled, (state, action) => {
      state.configLoading = false;
      state.ticketStatuses = action.payload.results || action.payload || [];
    });

    // Ticket Categories
    builder.addCase(loadTicketCategories.fulfilled, (state, action) => {
      state.ticketCategories = action.payload.results || action.payload || [];
    });

    // SLA Policies
    builder.addCase(loadSLAPolicies.fulfilled, (state, action) => {
      state.slaPolicies = action.payload.results || action.payload || [];
    });

    // KB Articles
    builder.addCase(loadKBArticles.pending, (state) => {
      state.kbLoading = true;
    });
    builder.addCase(loadKBArticles.fulfilled, (state, action) => {
      state.kbLoading = false;
      state.kbArticles = action.payload.results || [];
      if (action.payload.count) {
        state.kbPagination = {
          currentPage: action.payload.page || 1,
          totalPages: Math.ceil((action.payload.count || 0) / 20),
          totalCount: action.payload.count || 0
        };
      }
    });

    // Ticket Templates
    builder.addCase(loadTicketTemplates.pending, (state) => {
      state.templatesLoading = true;
    });
    builder.addCase(loadTicketTemplates.fulfilled, (state, action) => {
      state.templatesLoading = false;
      state.ticketTemplates = action.payload.results || action.payload || [];
    });
  }
});

export const { clearSelectedTicket } = supportSlice.actions;
export default supportSlice.reducer;
